import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'

import Container from 'login/Container/Container'

import PasswordRequirements from '../PasswordRequirements/PasswordRequirements'
import * as passwordValidators from '../../shared/passwordValidators'
import { useCurrentUser } from 'shared/lib/hooks'

import * as styles from './PasswordReset.m.css'

async function resetPassword(password: string) {
  const response = await fetch('/auth/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  })

  if (response.ok) {
    return true
  } else {
    return false
  }
}

async function forgotPassword(code: string, password: string) {
  const response = await fetch('/auth/forgot-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code, password }),
  })

  if (response.ok) {
    return true
  } else {
    return false
  }
}

async function validateResetCode(resetCode: string) {
  const response = await fetch(`/auth/reset-codes/${resetCode}`)

  if (response.ok) {
    return true
  } else {
    return false
  }
}

const params = new URLSearchParams(window.location.search)
const resetCode = params.get('c')

const PasswordReset: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [error, setError] = useState<string>('')
  const [passwordResetSuccessful, setPasswordResetSuccessful] =
    useState<boolean>(false)
  const isValidCode = useQuery(
    'validForgotCode',
    () => validateResetCode(resetCode),
    { staleTime: Infinity },
  )
  const currentUser = useCurrentUser()

  const isForgotPasswordReset = resetCode && isValidCode.data
  const isAuthedForcedReset = !!currentUser.data

  const isValidPassword =
    passwordValidators.isValidPassword(password) &&
    password === confirmPassword &&
    password.length > 0

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    const passwordResetSuccess = resetCode
      ? await forgotPassword(resetCode, password)
      : await resetPassword(password)

    if (passwordResetSuccess) {
      setPasswordResetSuccessful(true)
      setTimeout(() => {
        navigate('/')
      }, 5000)
    } else {
      setError(
        'There was an error resetting your password. Please try again later.',
      )
    }

    return false
  }

  return (
    <Container>
      {resetCode && !isValidCode.data && (
        <div>
          Invalid password reset link.{' '}
          <a onClick={() => navigate('/forgot-password')}>
            Click here to request a new one.
          </a>
        </div>
      )}
      {!resetCode && currentUser.isError && (
        <div>
          <p>
            You must be logged in or have a valid password reset code in order
            to reset your password.{' '}
            <a onClick={() => navigate('/forgot-password')}>
              Click here to request a password reset.
            </a>
            . Alertnatively, <a onClick={() => navigate('/')}>click here</a> to
            return to the login page.
          </p>
        </div>
      )}
      {(isForgotPasswordReset || isAuthedForcedReset) &&
        !passwordResetSuccessful && (
          <form onSubmit={handleSubmit} className={styles.passwordResetForm}>
            <div>
              You are required to reset your password before you can continue.
            </div>
            <input
              type="password"
              placeholder="New Password"
              onChange={(e) => setPassword(e.target.value)}
            />
            <input
              type="password"
              placeholder="Confirm New Password"
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <PasswordRequirements
              password={password}
              confirmPassword={confirmPassword}
            />
            <button
              type="submit"
              disabled={!isValidPassword}
              className={styles.button}
            >
              Reset Password
            </button>
            {error.length > 0 && <div className={styles.error}>{error}</div>}
          </form>
        )}
      {passwordResetSuccessful && (
        <div>
          Password reset was successful. You'll need to log in again. You will
          be redirected to the <a onClick={() => navigate('/')}>login page</a>{' '}
          in a few seconds.
        </div>
      )}
    </Container>
  )
}

export default PasswordReset
